import React, { useState } from 'react';
import type {
  Calendar,
  Candidate,
  TimeZone,
  TimeZoneIana,
} from '@axiom/validation';
import {
  Button,
  CalendarTimeZoneUtil,
  CondensedLarge,
  FluidButtonLayout,
  Form,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  ParagraphHeader,
  TimezoneDropdown,
  Well,
} from '@axiom/ui';

import { CalendarApi } from '../../api/calendar-api';

import { TalentTimezoneEditorSchema } from './talent-timezone-editor-schema';

export type TalentTimezoneEditorMode = 'set' | 'edit';

export type TalentTimezoneEditorProps = {
  candidateId: Candidate['id'];
  calendarId: Calendar['id'] | null;
  mode?: TalentTimezoneEditorMode;
  currentTimezone?: TimeZone;
  onSaved: (timezone: TimeZoneIana) => void | Promise<void>;
};

export const TalentTimezoneEditor = ({
  candidateId,
  calendarId,
  mode = 'set',
  currentTimezone,
  onSaved,
}: TalentTimezoneEditorProps) => {
  const [isEditing, setIsEditing] = useState(mode === 'set');

  const showPencil = mode === 'edit' && !isEditing;

  return (
    <Well name="TALENT_AVAILABILITY_ZONE">
      <Layout position="middle">
        <LayoutItem position="left">
          <ParagraphHeader name="TALENT_TIMEZONE_HEADER">
            Talent's timezone:
          </ParagraphHeader>
        </LayoutItem>
        {showPencil ? (
          <LayoutItem position="right" fluid>
            <IconButton
              name="EDIT_TIMEZONE_BUTTON"
              pattern="secondary"
              variation="minimal"
              icon="pencil"
              onClick={() => {
                setIsEditing(true);
              }}
            />
          </LayoutItem>
        ) : null}
      </Layout>
      {mode === 'edit' && !isEditing ? (
        <CondensedLarge>
          {currentTimezone
            ? CalendarTimeZoneUtil.getDisplayZone(currentTimezone)
            : ''}
        </CondensedLarge>
      ) : (
        <Form
          name="TALENT_TIMEZONE_EDITOR_FORM"
          schema={TalentTimezoneEditorSchema}
          initialValues={{
            TIMEZONE:
              mode === 'edit' && currentTimezone ? currentTimezone.id : null,
          }}
          onSubmit={async ({ TIMEZONE }) => {
            const { iana } = CalendarTimeZoneUtil.getZoneFromId(
              TIMEZONE ?? undefined
            );

            await (calendarId
              ? CalendarApi.updateCalendar(calendarId, { timezone: iana })
              : CalendarApi.createCalendar({
                  candidateId,
                  timezone: iana,
                }));
            // Intentionally do NOT invalidate the calendar cache here.
            // The parent modal subscribes to /calendars/{id} via useApi; a
            // mid-modal refetch reinitializes the form (enableReinitialize)
            // and erases the user's in-progress unsaved interview slot,
            // which reads as flicker. Parent owns the refresh via its
            // refreshData() at modal close time.

            // Always exit editing — once the parent's candidate cache
            // refreshes (in onSaved), the mode flips set→edit and we
            // want to land in the read-only view, not the form.
            setIsEditing(false);

            await onSaved(iana);
          }}
        >
          {({ fireSubmit }) => (
            <Layout direction="vertical">
              <LayoutItem fluid>
                <TimezoneDropdown
                  name="TIMEZONE"
                  label={
                    mode === 'edit'
                      ? "Update talent's time zone"
                      : "Set talent's time zone"
                  }
                />
              </LayoutItem>
              <LayoutItem>
                <Gutter top="24px">
                  <FluidButtonLayout>
                    {mode === 'edit' ? (
                      <Button
                        name="CANCEL_TIMEZONE_BUTTON"
                        pattern="secondary"
                        variation="outline"
                        onClick={() => {
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                    <Button
                      name="SAVE_TIMEZONE_BUTTON"
                      pattern="primary"
                      onClick={fireSubmit}
                    >
                      Save
                    </Button>
                  </FluidButtonLayout>
                </Gutter>
              </LayoutItem>
            </Layout>
          )}
        </Form>
      )}
    </Well>
  );
};
